import { useCallback, useMemo, useRef, useState, useEffect } from 'react';

type Slots = {
  leftIdx: number | null;
  currentIdx: number;
  rightIdx: number | null;
};

interface Params {
  count: number;
  activeIdx: number;
  setActiveIdx: React.Dispatch<React.SetStateAction<number>>;
  isEnabled: boolean;
  swipeThresholdPx?: number;
  onCommit?: (nextIdx: number) => void;
}

const ANIM_MS = 260;
const PEEK = 10;
const GUTTER = 12;

export function useSwipeDeck({
  count,
  activeIdx,
  setActiveIdx,
  isEnabled,
  swipeThresholdPx = 50,
  onCommit
}: Params) {
  const stageRef = useRef<HTMLDivElement | null>(null);

  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const dxPxRef = useRef(0);
  const dragging = useRef(false);
  const ignoreThisGesture = useRef(false);

  const [stageW, setStageW] = useState(0);
  const [slidePx, setSlidePx] = useState(0);
  const stepPx = useMemo(() => slidePx + GUTTER, [slidePx]);

  const baseTx = useMemo(() => {
    if (!stageW || !slidePx) return 0;
    return stageW / 2 - (1.5 * slidePx + GUTTER);
  }, [stageW, slidePx]);

  const [txPx, setTxPx] = useState(0);
  const [withTransition, setWithTransition] = useState(false);

  const canPrev = activeIdx > 0;
  const canNext = activeIdx < count - 1;

  const slots: Slots = useMemo(
    () => ({
      leftIdx: canPrev ? activeIdx - 1 : null,
      currentIdx: activeIdx,
      rightIdx: canNext ? activeIdx + 1 : null
    }),
    [activeIdx, canPrev, canNext]
  );

  const measure = useCallback(() => {
    if (!stageRef.current) return;
    const w = stageRef.current.clientWidth || 0;
    setStageW(w);
    setSlidePx(Math.max(1, w - 2 * (PEEK + GUTTER)));
  }, []);

  useEffect(() => {
    if (!isEnabled) return;
    measure();
    setWithTransition(false);
    requestAnimationFrame(() => setTxPx(baseTx));

    const onResize = () => {
      measure();
      requestAnimationFrame(() => {
        setWithTransition(false);
        setTxPx(baseTx);
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isEnabled, measure, baseTx]);

  const resetTouch = () => {
    startX.current = null;
    startY.current = null;
    dxPxRef.current = 0;
    dragging.current = false;
    ignoreThisGesture.current = false;
  };

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isEnabled) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-ignore-parent-swipe="true"]')) {
        ignoreThisGesture.current = true;
        return;
      }
      if (!stageRef.current) return;
      if (!stageW || !slidePx) {
        const w = stageRef.current.clientWidth || 0;
        setStageW(w);
        setSlidePx(Math.max(1, w - 2 * (PEEK + GUTTER)));
      }
      const t = e.touches[0];
      startX.current = t.clientX;
      startY.current = t.clientY;
      dragging.current = true;
      setWithTransition(false);
    },
    [isEnabled, stageW, slidePx]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isEnabled || !dragging.current || ignoreThisGesture.current) return;
      if (startX.current == null || startY.current == null) return;

      const t = e.touches[0];
      const dx = t.clientX - startX.current;
      const dy = t.clientY - startY.current;
      dxPxRef.current = dx;

      if (Math.abs(dy) > Math.abs(dx)) {
        setTxPx(baseTx);
        return;
      }

      let next = baseTx + dx;
      if (dx > 0) {
        if (!canPrev) next = baseTx;
        else if (dx > stepPx) next = baseTx + stepPx;
      } else if (dx < 0) {
        if (!canNext) next = baseTx;
        else if (dx < -stepPx) next = baseTx - stepPx;
      }
      setTxPx(next);
    },
    [isEnabled, baseTx, canPrev, canNext, stepPx]
  );

  const onTouchEnd = useCallback(() => {
    if (!isEnabled || ignoreThisGesture.current || !dragging.current) {
      resetTouch();
      return;
    }

    const passed = Math.abs(dxPxRef.current) > swipeThresholdPx;
    setWithTransition(true);

    if (!passed) {
      setTxPx(baseTx);
      setTimeout(() => setWithTransition(false), ANIM_MS);
      resetTouch();
      return;
    }

    if (dxPxRef.current < 0 && canNext) {
      setTxPx(baseTx - stepPx);
      setTimeout(() => {
        setWithTransition(false);
        setActiveIdx(i => {
          const n = Math.min(i + 1, count - 1);
          onCommit?.(n);
          return n;
        });
        setTxPx(baseTx);
      }, ANIM_MS);
    } else if (dxPxRef.current > 0 && canPrev) {
      setTxPx(baseTx + stepPx);
      setTimeout(() => {
        setWithTransition(false);
        setActiveIdx(i => {
          const n = Math.max(i - 1, 0);
          onCommit?.(n);
          return n;
        });
        setTxPx(baseTx);
      }, ANIM_MS);
    } else {
      setTxPx(baseTx);
      setTimeout(() => setWithTransition(false), ANIM_MS);
    }
    resetTouch();
  }, [
    isEnabled,
    swipeThresholdPx,
    canNext,
    canPrev,
    baseTx,
    stepPx,
    setActiveIdx,
    count,
    onCommit
  ]);

  return {
    stageRef,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    withTransition,
    txPx,
    slidePx,
    slots
  };
}
